import { ReactElement, useCallback, useMemo, useState } from 'react'
import { Box, Typography } from '@mui/material'
import ElementListSearchBar from '@editor/components/ElementListPane/components/ElementListSearchBar'
import usePlanImageCardEditMode from '@editor/hooks/usePlanImageCardEditMode'
import { usePlanImageCardStore } from '@editorStores'
import { useSelectedPlanElementId } from '@editorHooks'
import type { PlanImageCard } from 'src/domain/planImageCards/types'
import ImageCardRow from './ImageCardRow'

const matchesSearch = (card: PlanImageCard, search: string): boolean => {
  if (!search) return true
  const normalized = search.trim().toLowerCase()
  return (
    card.name.toLowerCase().includes(normalized) ||
    card.titleLines.join(' ').toLowerCase().includes(normalized)
  )
}

const ImageCardsListPane = (): ReactElement => {
  const cards = usePlanImageCardStore(state => state.cards)
  const selectedId = useSelectedPlanElementId('image-card')
  const placeholderCardIds = usePlanImageCardStore(state => state.placeholderCardIds)
  const { enterPlanImageCardEdit } = usePlanImageCardEditMode()

  const [search, setSearch] = useState('')

  const filteredCards = useMemo(
    () => cards.filter(card => matchesSearch(card, search)),
    [cards, search],
  )

  const handleSelect = useCallback(
    (id: string) => enterPlanImageCardEdit(id),
    [enterPlanImageCardEdit],
  )

  return (
    <Box
      data-cy="image-cards-list-pane"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        backgroundColor: 'common.white',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1,
          py: 0.75,
          borderBottom: theme => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <ElementListSearchBar value={search} onChange={setSearch} />
        </Box>
      </Box>

      {filteredCards.length === 0 ? (
        <Box
          sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}
        >
          <Typography variant="body2" color="text.secondary" data-cy="image-cards-list-empty">
            {cards.length === 0 ? 'Keine Plan-Bilder' : 'Keine Treffer'}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {filteredCards.map(card => (
            <ImageCardRow
              key={card.id}
              planImageCard={card}
              selected={selectedId === card.id}
              isPlaceholder={placeholderCardIds.includes(card.id)}
              onSelect={handleSelect}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}

export default ImageCardsListPane
